'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

/** Degradado ember de firma del tema brasa (CTA y palabra encendida). */
const EMBER = 'linear-gradient(100deg, #ffa24d 0%, #ff5f2e 52%, #e0401f 100%)'

/** Nombre con la última palabra "encendida" en ember (bg-clip). Con nombre de
 *  una sola palabra, se enciende completa. */
function EmberName({ name }: { name: string }) {
  const words = name.trim().split(/\s+/)
  const last = words.pop() ?? ''
  const emberStyle = {
    background: EMBER,
    WebkitBackgroundClip: 'text' as const,
    WebkitTextFillColor: 'transparent' as const,
    backgroundClip: 'text' as const,
  }
  if (words.length === 0) return <span style={emberStyle}>{last}</span>
  return (
    <>
      {words.join(' ')} <span style={emberStyle}>{last}</span>
    </>
  )
}

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Hero del tema "brasa" (carbón y ember): asador / cocina de fuego. Centrado
 * sobre carbón ceniza con un rescoldo radial naranja subiendo desde el pie,
 * nombre en Gloock con la última palabra en degradado ember y el CTA como
 * ÚNICO elemento encendido (fondo ember con glow cálido) — la disciplina del
 * tema es un solo fuego a la vez. Lee tokens del tema (--ms-*).
 */
export function HeroBrasa({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Rescoldo: ember radial desde el pie + viñeta para profundidad */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(90% 46% at 50% 108%, rgba(255,95,46,0.28), transparent 60%)' }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(82% 66% at 50% 44%, transparent 48%, rgba(10,6,4,0.55) 100%)' }}
      />

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(680px, 88vh)' }}
      >
        {city && (
          <span className="text-[11px] font-semibold uppercase tracking-[0.36em] pl-[0.36em]" style={{ color: 'var(--ms-accent)' }}>
            {city}
          </span>
        )}

        <h1
          className="mt-6 font-normal leading-[0.98] tracking-[0.005em] text-[clamp(48px,10vw,108px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 6px 44px rgba(0,0,0,0.65)' : undefined,
          }}
        >
          <EmberName name={businessName} />
        </h1>

        {tagline && (
          <p className="mt-5 max-w-xl text-[16px] sm:text-[18px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        {/* Meta discreta */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: ember encendido (el único fuego) + outline ember */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition hover:brightness-110 active:scale-[0.98]"
            style={{
              background: EMBER,
              color: '#1a0c06',
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 18px 48px -14px rgba(255,95,46,0.55)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.16em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid rgba(255,95,46,0.5)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
